'use client';
import { CSSProperties, HTMLAttributes, ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { pxToRem } from '@intigral/utils/lib/helpers/pxToRem';
import styles from './stickylabel.module.sass';

export type StickyLabelAnchor =
    | 'top'
    | 'bottom'
    | 'top-start'
    | 'top-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'center-start'
    | 'center-end';

type Length = number | string;

export interface StickyLabelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'onError'> {
    src?: string;
    alt?: string;
    children?: ReactNode;
    anchor?: StickyLabelAnchor;
    offset?: Length | { block?: Length; inline?: Length };
    fixed?: boolean;
    width?: Length;
    height?: Length;
    zIndex?: number;
    interactive?: boolean;
    reveal?: 'fade' | 'none';
    onError?: () => void;
}

const toCss = (value?: Length) => (typeof value === 'number' ? pxToRem(value) : value);

export const StickyLabel = ({
    src,
    alt,
    children,
    anchor = 'center-end',
    offset,
    fixed,
    width,
    height,
    zIndex,
    interactive,
    reveal = 'none',
    onError,
    className,
    style,
    ...props
}: StickyLabelProps) => {
    const imgRef = useRef<HTMLImageElement>(null);
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const [settled, setSettled] = useState<{ src?: string; ok: boolean }>();

    const current = settled?.src === src ? settled : undefined;
    const loaded = current?.ok === true;
    const failed = current?.ok === false;

    useEffect(() => {
        // hydrated images can settle before React attaches onLoad/onError
        const img = imgRef.current;
        if (img?.complete) setSettled({ src, ok: img.naturalWidth > 0 });
    }, [src]);

    useEffect(() => {
        if (failed) onErrorRef.current?.();
    }, [failed, src]);

    const showImage = !!src && !failed;
    if (!showImage && !children) return null;

    const block = typeof offset === 'object' ? offset.block : offset;
    const inline = typeof offset === 'object' ? offset.inline : offset;

    const inlineStyles = {
        width: toCss(width),
        height: toCss(height),
        zIndex,
        '--sticky-block': toCss(block),
        '--sticky-inline': toCss(inline),
        ...style,
    } as CSSProperties;

    return (
        <div
            {...props}
            className={cn(
                styles.label,
                styles[anchor],
                fixed && styles.fixed,
                !interactive && styles.passive,
                reveal === 'fade' && styles.fade,
                reveal === 'fade' && (loaded || !showImage) && styles.revealed,
                className
            )}
            style={inlineStyles}
            aria-hidden={showImage && !alt && !children ? true : props['aria-hidden']}
        >
            {showImage && (
                <img
                    ref={imgRef}
                    src={src}
                    alt={alt ?? ''}
                    onLoad={() => setSettled({ src, ok: true })}
                    onError={() => setSettled({ src, ok: false })}
                />
            )}
            {children}
        </div>
    );
};
